import React, { useState, useEffect } from 'react';
import { getAdminPrivacyRequests, processPrivacyRequest } from '../../../services/privacyService';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


/**
 * Privacy Requests Management Panel
 * Admin view of GDPR/CCPA requests
 */
const PrivacyRequestsPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', requestType: '' });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  
  useEffect(() => {
    loadRequests();
  }, [filter]);
  
  const loadRequests = async () => {
    setLoading(true);
    const result = await getAdminPrivacyRequests(filter);
    if (result?.success) {
      setRequests(result?.data || []);
    }
    setLoading(false);
  };
  
  const handleProcess = async (action) => {
    if (!selectedRequest || !adminNotes) {
      alert('Please provide admin notes');
      return;
    }
    
    const result = await processPrivacyRequest(selectedRequest?.id, action, adminNotes);
    if (result?.success) {
      alert(`Request ${action}d successfully`);
      setSelectedRequest(null);
      setAdminNotes('');
      loadRequests();
    } else {
      alert(`Failed to process: ${result?.error}`);
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' }
    };
    
    const badge = badges?.[status] || badges?.pending;
    const Icon = badge?.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        <Icon className="w-3 h-3" />
        {badge?.label}
      </span>
    );
  };
  
  const getRequestTypeLabel = (type) => {
    const labels = {
      access: 'Data Access',
      erasure: 'Account Deletion',
      rectification: 'Data Rectification',
      portability: 'Data Portability',
      objection: 'Processing Objection'
    };
    return labels?.[type] || type;
  };
  
  const getDaysUntilDeadline = (deadlineDate) => {
    const days = Math.ceil((new Date(deadlineDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };
  
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex gap-4">
          <select
            value={filter?.status}
            onChange={(e) => setFilter({ ...filter, status: e?.target?.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          
          <select
            value={filter?.requestType}
            onChange={(e) => setFilter({ ...filter, requestType: e?.target?.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="access">Data Access</option>
            <option value="erasure">Account Deletion</option>
            <option value="rectification">Data Rectification</option>
            <option value="portability">Data Portability</option>
            <option value="objection">Processing Objection</option>
          </select>
        </div>
      </div>
      
      {/* Requests List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading requests...</div>
        ) : requests?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No privacy requests found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests?.map(request => {
                  const daysUntil = getDaysUntilDeadline(request?.deadline_date);
                  const isUrgent = daysUntil <= 7 && request?.request_status === 'pending';
                  
                  return (
                    <tr key={request?.id} className={isUrgent ? 'bg-red-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{request?.user?.full_name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{request?.user?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getRequestTypeLabel(request?.request_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request?.request_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request?.created_at)?.toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${
                          isUrgent ? 'text-red-600 font-semibold' : 'text-gray-500'
                        }`}>
                          {new Date(request?.deadline_date)?.toLocaleDateString()}
                          {request?.request_status === 'pending' && (
                            <div className="text-xs mt-1">
                              {daysUntil > 0 ? `${daysUntil} days left` : 'Overdue'}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request?.request_status === 'pending' && (
                          <Button
                            onClick={() => setSelectedRequest(request)}
                            size="sm"
                            variant="outline"
                          >
                            Process
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Process Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Process Privacy Request</h3>
            
            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-2">
                <strong>User:</strong> {selectedRequest?.user?.email}
              </div>
              <div className="text-sm text-gray-600 mb-2">
                <strong>Request Type:</strong> {getRequestTypeLabel(selectedRequest?.request_type)}
              </div>
              <div className="text-sm text-gray-600 mb-4">
                <strong>Deadline:</strong> {new Date(selectedRequest?.deadline_date)?.toLocaleDateString()}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes:
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e?.target?.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Provide details about how this request was processed..."
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setSelectedRequest(null);
                  setAdminNotes('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleProcess('reject')}
                variant="danger"
                className="flex-1"
                disabled={!adminNotes}
              >
                Reject Request
              </Button>
              <Button
                onClick={() => handleProcess('approve')}
                className="flex-1"
                disabled={!adminNotes}
              >
                Approve & Complete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyRequestsPanel;